import { useContext, useState, useEffect, useMemo } from "react";
import inputStyle from "../../styles/input.module.scss";
import { MaterialContext } from "../../context/MaterialContext";
import axios from "axios";
import DragDrop from "./DragDrop";
import Dropzone from "./Dropzone";
import { closeIcon } from "../../components/Icons";
import PreviewSwatch from "./PreviewSwatch";
import ImagePreviewFrame from "./ImagePreviewFrame";
import { authHeaders } from "../../utilities/AuthState";

const sameImageList = (left, right) =>
  JSON.stringify(left || []) === JSON.stringify(right || []);

function ImageAdd({
  vendor,
  slug,
  data,
  setMaterialArray,
  materialDescription,
}) {
  const { ROOT_SERVER, allMaterials, refetch } = useContext(MaterialContext);
  const [images, setImages] = useState([]);
  const [savedImages, setSavedImages] = useState(null);
  const [folder, setFolder] = useState([]);
  const [previewImage, setPreviewImage] = useState("");
  const [uploadedImages, setUploadedImages] = useState(false);
  const [isSavingSwatch, setIsSavingSwatch] = useState(false);
  const [swatchSaveError, setSwatchSaveError] = useState("");
  const [swatchSaved, setSwatchSaved] = useState(false);

  useEffect(() => {
    setMaterialArray(images);
  }, [images, setMaterialArray]);

  function getData({ refreshFolderOnly = false } = {}) {
    const materialGet = `${ROOT_SERVER}material/${vendor}/${slug}`;
    const folderGet = `${ROOT_SERVER}material/folder/${vendor}/${slug}`;

    if (refreshFolderOnly) {
      axios
        .get(folderGet, { headers: authHeaders() })
        .then((response) => {
          if (Array.isArray(response.data) && response.data.length) {
            setFolder(response.data);
          }
        })
        .catch((errors) => {
          console.log(errors);
        });
      return;
    }

    axios
      .all([
        axios.get(materialGet, { headers: authHeaders() }),
        axios.get(folderGet, { headers: authHeaders() }),
      ])
      .then(
        axios.spread((responseMaterials, responseFolder) => {
          const material = responseMaterials.data?.[0];
          const nextImages = Array.isArray(material?.images)
            ? material.images
            : [];
          setImages(nextImages);
          setSavedImages(nextImages);
          setPreviewImage(nextImages[0] || "");
          setSwatchSaveError("");
          setSwatchSaved(false);
          if (Array.isArray(responseFolder.data) && responseFolder.data.length) {
            setFolder(responseFolder.data);
          }
        }),
      )
      .catch((errors) => {
        console.log(errors);
      });
  }

  useEffect(() => {
    getData();
    // eslint-disable-next-line
  }, [data, vendor, slug]);

  useEffect(() => {
    if (uploadedImages) {
      getData({ refreshFolderOnly: true });
      setUploadedImages(false);
    }
    // eslint-disable-next-line
  }, [uploadedImages]);

  const unusedImages = useMemo(
    () => folder.filter((item) => !images.includes(item)),
    [folder, images],
  );
  const catalogMaterial = useMemo(
    () =>
      allMaterials.find(
        (item) => item.vendor === vendor && item.slug === slug,
      ) || null,
    [allMaterials, slug, vendor],
  );
  const swatchDirty =
    savedImages !== null && !sameImageList(images, savedImages);

  const handleSaveSwatch = async () => {
    if (!catalogMaterial?.id || isSavingSwatch || !swatchDirty) {
      return;
    }

    setIsSavingSwatch(true);
    setSwatchSaveError("");
    setSwatchSaved(false);

    try {
      const response = await axios.put(
        `${ROOT_SERVER}material/update/${catalogMaterial.id}`,
        {
          data: {
            ...catalogMaterial,
            images,
          },
        },
        {
          headers: {
            ...authHeaders(),
            "Content-Type": "application/json",
          },
        },
      );

      if (response.status !== 200) {
        throw new Error("swatch save failed");
      }

      setSavedImages(images);
      setSwatchSaved(true);
      if (typeof refetch === "function") {
        await refetch();
      }
    } catch (error) {
      setSwatchSaveError("Could not save swatch order. Try again.");
    } finally {
      setIsSavingSwatch(false);
    }
  };

  const handleAddImageFromFolder = (image) => {
    setImages((current) =>
      current.includes(image) ? current : [...current, image],
    );
    setPreviewImage(image);
  };

  const handleUploadSuccess = (uploadedNames) => {
    const nextNames = Array.isArray(uploadedNames) ? uploadedNames : [];
    // Keep uploads in staging (folder / unused). Do not auto-add to the swatch.
    if (nextNames[0]) {
      setPreviewImage(nextNames[0]);
    }
  };

  const handleDeleteFromFolder = (item) => {
    const confirmed = window.confirm(
      `Delete ${item} from the folder permanently? This cannot be undone.`,
    );
    if (!confirmed) {
      return;
    }

    axios
      .delete(`${ROOT_SERVER}upload/delete/${item}`, {
        headers: {
          "Content-Type": "application/json",
        },
        data: { vendor: vendor, slug: slug },
      })
      .then(() => {
        getData();
      });
  };

  return (
    <div className={inputStyle.imageWorkspace}>
      <div className={inputStyle.imageColumns}>
        <section className={inputStyle.imageColumn}>
          <div className={inputStyle.previewHeader}>
            <div className={inputStyle.previewSlot}>
              <p className={inputStyle.imagePreviewLabel}>Swatch preview</p>
              <div className={inputStyle.swatchView}>
                <PreviewSwatch
                  images={images}
                  vendor={vendor}
                  slug={slug}
                  materialDescription={materialDescription}
                />
              </div>
            </div>
            <div className={inputStyle.previewSlot}>
              <p className={inputStyle.imagePreviewLabel}>Image preview</p>
              <ImagePreviewFrame
                src={
                  previewImage
                    ? `${ROOT_SERVER}${vendor}/${slug}/swatch/${previewImage}`
                    : ""
                }
                alt={previewImage || "swatch preview"}
                emptyLabel="Click an image on the right to preview"
              />
            </div>
          </div>
        </section>

        <section className={inputStyle.imageColumn}>
          <p className={inputStyle.imageWorkflowNote}>
            Upload into staging, then push images into the swatch when you are
            ready. Save Swatch Array keeps this order without walking the
            details steps.
          </p>

          <div className={inputStyle.imageSectionHeader}>
            <div className={inputStyle.imageSectionHeadingRow}>
              <h4 className={inputStyle.imageSectionHeading}>
                In this swatch
              </h4>
              <button
                type="button"
                className={inputStyle.swatchSaveButton}
                onClick={handleSaveSwatch}
                disabled={!catalogMaterial?.id || !swatchDirty || isSavingSwatch}
              >
                {isSavingSwatch ? "Saving…" : "Save Swatch Array"}
              </button>
            </div>
            <p className={inputStyle.imageSectionNote}>
              These images are shown on the material. Click and drag to change
              order.
              {swatchDirty
                ? " Unsaved swatch changes."
                : swatchSaved
                  ? " Swatch saved."
                  : ""}
              {swatchSaveError ? ` ${swatchSaveError}` : ""}
            </p>
          </div>
          <DragDrop
            images={images}
            vendor={vendor}
            slug={slug}
            setImages={setImages}
            setPreviewImage={setPreviewImage}
          />

          <div
            className={`${inputStyle.imageSectionHeader} ${inputStyle.imageSectionHeaderSecondary}`}
          >
            <h4 className={inputStyle.imageSectionHeading}>Staging</h4>
            <p className={inputStyle.imageSectionNote}>
              Uploaded and unused folder files wait here. Use Add to swatch to
              push them into view.
            </p>
          </div>
          <div
            className={`${inputStyle.imagePool} ${inputStyle.imagePanel} ${inputStyle.imagePoolSecondary}`}
          >
            {unusedImages.length === 0 ? (
              <div className={inputStyle.dragDropEmpty}>
                Staging is empty — upload below to add files here
              </div>
            ) : (
              unusedImages.map((item, i) => (
                <div key={item} className={inputStyle.dragDropItem}>
                  <div
                    className={inputStyle.deleteDragDropItem}
                    onClick={() => handleDeleteFromFolder(item)}
                    title="Delete from folder"
                  >
                    <div className={inputStyle.deleteIcon}>{closeIcon}</div>
                  </div>
                  <img
                    src={`${ROOT_SERVER}${vendor}/${slug}/thumbnail/${item}`}
                    alt={item + "folder" + i}
                    onClick={() => setPreviewImage(item)}
                  />
                  <button
                    type="button"
                    className={inputStyle.folderItemAdd}
                    onClick={() => handleAddImageFromFolder(item)}
                  >
                    Add to swatch
                  </button>
                </div>
              ))
            )}
          </div>

          <div
            className={`${inputStyle.imageSectionHeader} ${inputStyle.imageSectionHeaderSecondary}`}
          >
            <h4 className={inputStyle.imageSectionHeading}>Upload</h4>
            <p className={inputStyle.imageSectionNote}>
              Drop or browse to stage files. They will not enter the swatch
              until you add them.
            </p>
          </div>
          <div
            className={`${inputStyle.imagePool} ${inputStyle.imagePanel} ${inputStyle.imagePoolCompact}`}
          >
            <Dropzone
              vendor={vendor}
              slug={slug}
              setUploadedImages={setUploadedImages}
              onUploadSuccess={handleUploadSuccess}
            />
          </div>
        </section>
      </div>
    </div>
  );
}

export default ImageAdd;
